import { AlertTriangle, FileSpreadsheet, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  IMPORT_STATUS_LABEL,
  type ImportRowStatus,
} from '@obraplan/core/domain/imports/table-mapping';
import {
  STRUCTURE_IMPORT_COLUMNS,
  type StructureColumnMapping,
  type StructureImportField,
} from '@obraplan/core/domain/structure/structure-import';
import type { StructureImportPreview } from '@obraplan/core/application/use-cases/structure/structure-import-use-cases';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';

const STATUS_TONE: Readonly<
  Record<ImportRowStatus, 'neutral' | 'ok' | 'info' | 'warn' | 'danger'>
> = {
  CREATE: 'ok',
  UPDATE: 'info',
  UNCHANGED: 'neutral',
  INVALID: 'danger',
  DUPLICATE: 'warn',
};

export interface StructureImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preview: StructureImportPreview | undefined;
  onFile: (file: File) => void;
  onRemap: (mapping: StructureColumnMapping, createMissingDisciplines: boolean) => void;
  onConfirm: (createMissingDisciplines: boolean) => void;
  isPending: boolean;
  errorMessage: string | undefined;
}

/**
 * Importar a estrutura da obra de uma planilha.
 *
 * Uma linha por atividade: torre, pavimento, nível e disciplina. Torre e pavimento
 * repetidos entre linhas são o mesmo registro — a planilha do escritório é assim, e
 * pedir uma aba por nível seria pedir que ela fosse reescrita.
 *
 * Disciplina fora do catálogo pode ser criada aqui mesmo, marcando a opção: recusar e
 * mandar cadastrar em outra tela obrigava a abandonar a importação e escolher o arquivo
 * de novo. Ela nasce **na obra**, nunca no catálogo global.
 *
 * O terceiro diálogo com esta forma (materiais, equipe e agora estrutura). O mapeamento
 * de colunas já é código comum em `domain/imports`; se aparecer um quarto, a casca visual
 * também vira componente — dois iguais é coincidência, três é padrão, quatro é dívida.
 */
export function StructureImportDialog({
  open,
  onOpenChange,
  preview,
  onFile,
  onRemap,
  onConfirm,
  isPending,
  errorMessage,
}: StructureImportDialogProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [createMissingDisciplines, setCreateMissingDisciplines] = useState(false);

  // A marca vale para a pré-visualização que está na tela. Arquivo novo — ou diálogo
  // reaberto — é outra pré-visualização, calculada sem ela: manter a caixa marcada diria
  // que as disciplinas serão criadas enquanto as linhas voltam a aparecer recusadas.
  useEffect(() => {
    setCreateMissingDisciplines(false);
  }, [open, preview?.fileName]);

  const mapping = preview?.mapping ?? {};
  const missingRequired = STRUCTURE_IMPORT_COLUMNS.filter(
    (column) => column.required && mapping[column.field] === undefined,
  );

  const changeMapping = (field: StructureImportField, header: string): void => {
    const next: StructureColumnMapping = { ...mapping };
    if (header === '') delete next[field];
    else next[field] = header;
    onRemap(next, createMissingDisciplines);
  };

  const changeCreateDisciplines = (value: boolean): void => {
    setCreateMissingDisciplines(value);
    onRemap(mapping, value);
  };

  const applicable = preview?.summary.create ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Importar estrutura da obra</DialogTitle>
          <DialogDescription>
            Uma linha por atividade: torre, pavimento e disciplina. O que faltar é criado — torre e
            pavimento repetidos entre linhas são o mesmo registro.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3 rounded-md border border-(--color-border-subtle) bg-(--color-surface-muted) p-3">
            <FileSpreadsheet className="size-5 text-(--color-content-muted)" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              {preview === undefined ? (
                <p className="text-sm text-(--color-content-muted)">
                  Nenhum arquivo escolhido. A primeira linha deve ter os títulos das colunas.
                </p>
              ) : (
                <p className="truncate text-sm">
                  <strong>{preview.fileName}</strong>{' '}
                  <span className="text-(--color-content-muted)">
                    · {preview.format} · {preview.summary.total} linhas
                  </span>
                </p>
              )}
            </div>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,.tsv,.txt,.xlsx"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file !== undefined) onFile(file);
                event.target.value = '';
              }}
            />
            <Button type="button" variant="secondary" onClick={() => fileInput.current?.click()}>
              <Upload aria-hidden="true" />
              {preview === undefined ? 'Escolher arquivo' : 'Trocar arquivo'}
            </Button>
          </div>

          {preview !== undefined && (
            <>
              <div>
                <h3 className="mb-2 text-sm font-medium">Colunas do arquivo</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {STRUCTURE_IMPORT_COLUMNS.map((column) => (
                    <Field
                      key={column.field}
                      label={column.label}
                      htmlFor={`map-str-${column.field}`}
                      required={column.required}
                      {...(column.field === 'level'
                        ? { hint: 'Sem esta coluna, a ordem do arquivo define o nível.' }
                        : {})}
                    >
                      <select
                        id={`map-str-${column.field}`}
                        className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-3 text-sm"
                        value={mapping[column.field] ?? ''}
                        onChange={(event) => changeMapping(column.field, event.target.value)}
                      >
                        <option value="">— não importar —</option>
                        {preview.headers.map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ))}
                </div>

                {missingRequired.length > 0 && (
                  <p
                    role="alert"
                    className="mt-2 flex items-center gap-2 text-sm text-(--color-state-danger)"
                  >
                    <AlertTriangle className="size-4" aria-hidden="true" />
                    Indique qual coluna traz{' '}
                    {missingRequired.map((column) => column.label.toLowerCase()).join(' e ')}.
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="ok">{preview.summary.create} atividades novas</Badge>
                {preview.summary.unchanged > 0 && (
                  <Badge tone="neutral">{preview.summary.unchanged} já existem</Badge>
                )}
                {preview.summary.duplicate > 0 && (
                  <Badge tone="warn">{preview.summary.duplicate} repetidas no arquivo</Badge>
                )}
                {preview.summary.invalid > 0 && (
                  <Badge tone="danger">{preview.summary.invalid} com erro</Badge>
                )}
              </div>

              {(preview.newTowers.length > 0 ||
                preview.newFloors.length > 0 ||
                preview.unknownDisciplines.length > 0) && (
                <div className="flex flex-col gap-1 rounded-md border border-(--color-border-subtle) p-3 text-sm">
                  {preview.newTowers.length > 0 && (
                    <p>
                      <span className="font-medium">Torres novas:</span>{' '}
                      {preview.newTowers.join(', ')}
                    </p>
                  )}
                  {preview.newFloors.length > 0 && (
                    <p>
                      <span className="font-medium">
                        Pavimentos novos ({preview.newFloors.length}):
                      </span>{' '}
                      {preview.newFloors.slice(0, 12).join(' · ')}
                      {preview.newFloors.length > 12 ? ' …' : ''}
                    </p>
                  )}
                  {preview.unknownDisciplines.length > 0 && (
                    <p className={createMissingDisciplines ? '' : 'text-(--color-state-danger)'}>
                      <span className="font-medium">Disciplinas fora do catálogo:</span>{' '}
                      {preview.unknownDisciplines.join(', ')}
                      {createMissingDisciplines
                        ? ' — serão criadas nesta obra.'
                        : ' — marque abaixo para criá-las, ou cadastre-as antes.'}
                    </p>
                  )}
                </div>
              )}

              <TableWrapper className="max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Linha</TableHead>
                      <TableHead>Situação</TableHead>
                      <TableHead>Torre</TableHead>
                      <TableHead>Pavimento</TableHead>
                      <TableHead className="w-16">Nível</TableHead>
                      <TableHead>Disciplina</TableHead>
                      <TableHead>Observação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.rows.slice(0, 200).map((row) => (
                      <TableRow key={row.rowNumber}>
                        <TableCell className="tabular text-(--color-content-muted)">
                          {row.rowNumber}
                        </TableCell>
                        <TableCell>
                          <Badge tone={STATUS_TONE[row.status]}>
                            {IMPORT_STATUS_LABEL[row.status]}
                          </Badge>
                        </TableCell>
                        <TableCell>{row.towerName || '—'}</TableCell>
                        <TableCell>{row.floorName || '—'}</TableCell>
                        <TableCell className="tabular">
                          {row.level}
                          {row.levelFromFile ? '' : '*'}
                        </TableCell>
                        <TableCell>{row.disciplineName || '—'}</TableCell>
                        <TableCell className="text-xs text-(--color-content-muted)">
                          {row.issues.map((issue) => issue.message).join(' ')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableWrapper>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={createMissingDisciplines}
                  onChange={(event) => changeCreateDisciplines(event.target.checked)}
                />
                Criar as disciplinas que faltam, somente nesta obra
              </label>

              <p className="text-xs text-(--color-content-muted)">
                Nível marcado com <strong>*</strong> foi deduzido da ordem do arquivo.
                {preview.rows.length > 200
                  ? ` Mostrando as 200 primeiras linhas; a importação considera todas as ${String(preview.summary.total)}.`
                  : ''}
              </p>
            </>
          )}

          {errorMessage !== undefined && (
            <p role="alert" className="text-sm text-(--color-state-danger)">
              {errorMessage}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={
              isPending || preview === undefined || applicable === 0 || missingRequired.length > 0
            }
            onClick={() => onConfirm(createMissingDisciplines)}
          >
            {isPending ? 'Importando…' : `Importar ${String(applicable)} atividades`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
